/* eslint-disable import/extensions */
/** @module Auth0LocalStorageHelper */

import Auth0Constants from './auth0-constants.mjs';
import Auth0CookieHelper from './auth0-cookie-helper.mjs';
import Auth0Utils from './auth0-utils-browser.mjs';

const localStorageCookieTtlSec = 90;

const sessionProperties = {
  clientId: null,
  audience: null,
  getClientId() {
    if (!this.clientId) {
      console.warn('no clientId set in Auth0ClientAndAudienceHelper');
    }
    return this.clientId;
  },
  setClientId(clientId) {
    this.clientId = clientId;
  },
  getAudience() {
    if (!this.audience) {
      console.warn('no audience set in Auth0ClientAndAudienceHelper');
    }
    return this.audience;
  },
  setAudience(audience) {
    this.audience = audience;
  },
};

class Auth0LocalStorageHelper {
  /**
   * When auth0-spajs writes tokens to localstorage, it uses the following patterns:
   *
   * Access and Refresh Tokens Key Name: @@auth0spajs@@::[Auth0 Client ID for App]::[audience]::[scope]
   * Identity Token Key Name: @@auth0spajs@@::[Auth0 Client ID for App]::@@user@@
   *
   * Auth0 Local Storage Key Names Examples:
   *
   * Access and Refresh Tokens Key Name: @@auth0spajs@@::EG0SS7ycoEQhwZIzmVegQPSSCdM0u5OM::ss:webapi::openid profile email offline_access
   * Identity Token Key Name: @@auth0spajs@@::EG0SS7ycoEQhwZIzmVegQPSSCdM0u5OM::@@user@@
   *
   * NOTES: Because these local storage entries are too large to exists as cookies in their native form, only the encoded tokens are stored
   * The "extra" data that is not stored in the transient cookie can all be derived from the token itself. These localStorage entries are more
   * like data structures supporting "easy access" to jwt data. Because the integrity of the rebuilt entires has been verified and tested, the
   * only issue to look out for here is if the schema of either of these entries changes. It is doubtful that it will though because auth0-spajs must
   * be able to understand entries left by possibly older versions of the package (and the entires do not include any kind of schema info)
   *
   * BUT... if this is problematic, an alternate approach would be to gather all of the entry data from localStorage, into one object,
   * stringify that object and split it into 4k chunks before writing. This will likely require 3 - 4 individual cookies
   * Obviously, the intent here is that the restore method would splice these entries back together before parsing the JSON.
   *
   * This is ultra-jank, but would likely work ok.
   */

  static get identityTokenRegex() {
    const clientId = sessionProperties.getClientId();
    return this._getIdentityTokenRegex(clientId);
  }

  static _getIdentityTokenRegex(clientId) {
    clientId = clientId ? Auth0Utils.escapeRegExp(clientId) : '\\S+';
    return new RegExp(`^\\@\\@auth0spajs\\@\\@::(${clientId})::\\@\\@user\\@\\@$`);
  }

  static get accessAndRefreshTokenRegexGeneric() {
    return this._getAccessAndRefreshTokenRegex();
  }

  static _getAccessAndRefreshTokenRegex = (clientId = null, audience = null) => {
    clientId = clientId ? Auth0Utils.escapeRegExp(clientId) : '\\S+';
    audience = audience ? Auth0Utils.escapeRegExp(audience) : '\\S+';
    return new RegExp(`^\\@\\@auth0spajs\\@\\@::(${clientId})::(${audience})::(.+)$`);
  };

  static get isAuth0TokenRegex() {
    return new RegExp('^\\@\\@auth0spajs\\@\\@::');
  }

  static saveAuth0TokensToCookie = (overrides = null) => {
    console.log('Auth0LocalStorageHelper => saveAuth0TokensToCookie');
    const accessToken = this._getAuth0AccessTokenStorageObject(overrides);
    const idToken = this._getAuth0IdentityTokenStorageObject(overrides);

    if (!accessToken || !idToken) {
      console.log('Auth0LocalStorageHelper => saveAuth0TokensToCookie invalid state, not persisting...');
      return;
    }

    console.log(`Auth0LocalStorageHelper => accessToken: ${accessToken}`);
    console.log(`Auth0LocalStorageHelper => idToken: ${idToken}`);

    Auth0CookieHelper.setCookieValueWithExpirationInSeconds(
      Auth0Constants.AUTH0_LOCALSTORAGE_COOKIE_NAME_AT,
      accessToken,
      localStorageCookieTtlSec,
    );
    Auth0CookieHelper.setCookieValueWithExpirationInSeconds(
      Auth0Constants.AUTH0_LOCALSTORAGE_COOKIE_NAME_IDT,
      idToken,
      localStorageCookieTtlSec,
    );
  };

  static appendAuth0TokensAsUrlFragment = (baseUrl, overrides = null) => {
    return `${baseUrl}#${this.getAuth0TokensAsSearchParams(overrides).toString()}`;
  };

  static getAuth0TokensAsSearchParams = (overrides = null) => {
    console.log('Auth0LocalStorageHelper => getAuth0TokensAsSearchParams');
    const accessToken = this._getAuth0AccessTokenStorageObject(overrides);
    const idToken = this._getAuth0IdentityTokenStorageObject(overrides);

    if (!accessToken || !idToken) {
      console.log('Auth0LocalStorageHelper => getAuth0TokensAsSearchParams invalid state...');
      return;
    }

    console.log(`Auth0LocalStorageHelper => accessToken: ${accessToken}`);
    console.log(`Auth0LocalStorageHelper => idToken: ${idToken}`);

    const urlObj = {};
    urlObj[Auth0Constants.AUTH0_LOCALSTORAGE_COOKIE_NAME_AT] = JSON.stringify(accessToken);
    urlObj[Auth0Constants.AUTH0_LOCALSTORAGE_COOKIE_NAME_IDT] = JSON.stringify(idToken);

    const sp = new URLSearchParams(urlObj);
    console.log(`Auth0LocalStorageHelper => sp: ${sp.toString()}`);
    return sp;
  };

  static _getAuth0AccessTokenStorageObject = (overrides = null) => {
    const accessToken = overrides?.accessTokenEntry || this.accessTokenEntry;
    if (!accessToken) {
      console.log('Auth0LocalStorageHelper => _auth0AccessTokenStorageObject invalid state, not persisting...');
      return;
    }
    return {
      entryName: accessToken.entryName,
      accessToken: accessToken.entryValue.body.access_token,
      refreshToken: accessToken.entryValue.body.refresh_token,
    };
  };

  static _getAuth0IdentityTokenStorageObject = (overrides = null) => {
    const idToken = overrides?.identityTokenEntry || this.identityTokenEntry;

    if (!idToken) {
      console.log('Auth0LocalStorageHelper => _getAuth0IdentityTokenStorageObject invalid state, not persisting...');
      return;
    }
    return {
      entryName: idToken.entryName,
      identityToken: idToken.entryValue.id_token,
    };
  };

  static deleteIdentityToken = (auth0ClientId) => {
    console.log('Auth0LocalStorageHelper => deleteIdentityToken', auth0ClientId);
    const idt = this.identityTokenEntry;
    if (!idt) {
      console.log('Auth0LocalStorageHelper.deleteIdentityToken => no identity was found...');
      return;
    }

    if (!auth0ClientId) {
      console.log('Auth0LocalStorageHelper.deleteIdentityToken => delete token entry (no clientId specified)', idt.entryName);
      localStorage.removeItem(idt.entryName);
      return;
    }

    if (idt.entryValue.decodedToken.claims.aud === auth0ClientId) {
      console.log(`Auth0LocalStorageHelper.deleteIdentityToken => delete token entry matching client_id ${auth0ClientId}`, idt.entryName);
      localStorage.removeItem(idt.entryName);
      return;
    }

    console.log(`Auth0LocalStorageHelper.deleteIdentityToken => no token matching client_id ${auth0ClientId} was found...`);
  };

  static deleteAccessToken = (auth0ClientId, audience = Auth0Constants.AUTH0_DEFAULT_AUDIENCE) => {
    console.log('Auth0LocalStorageHelper => deleteAccessToken', auth0ClientId, audience);
    const regex = this._getAccessAndRefreshTokenRegex(auth0ClientId, audience);
    const foundAt = this._getAuth0Token(regex);

    if (!foundAt) {
      console.log(`Auth0LocalStorageHelper.deleteAccessToken => no token matching ${regex} was found...`);
      return;
    }

    console.log(`Auth0LocalStorageHelper.deleteAccessToken => delete token entry matching client_id ${auth0ClientId}`, foundAt.entryName);
    localStorage.removeItem(foundAt.entryName);
  };

  static deleteAllTokens = (auth0ClientId, audience) => {
    this.deleteAccessToken(auth0ClientId, audience);
    this.deleteIdentityToken(auth0ClientId);
  };

  static resetAuth0State = () => {
    console.log('Auth0LocalStorageHelper => resetAuth0State');
    let allAuth0Tokens = this._getAuth0Tokens(this.accessAndRefreshTokenRegexGeneric);
    const idt = this.identityTokenEntry;
    if (idt) {
      allAuth0Tokens = [
        ...allAuth0Tokens,
        idt,
      ];
    }

    allAuth0Tokens.forEach((lsToken) => {
      console.log('Auth0LocalStorageHelper.resetAuth0State => delete token entry', lsToken);
      localStorage.removeItem(lsToken.entryName);
    });
  };

  static _getAuth0Token = (regexTest, returnAsString = false) => {
    console.log(`Auth0LocalStorageHelper => _getAuth0Token for ${regexTest.toString()}`);
    for (let i = 0; i < localStorage.length; i++) {
      const tokenKey = localStorage.key(i);
      if (regexTest.test(tokenKey)) {
        console.log(`=> found Auth0 key match for ${regexTest.toString()} => ${tokenKey}`);
        const tokenValStr = localStorage.getItem(tokenKey);
        return {
          entryName: tokenKey,
          entryValue: returnAsString
            ? tokenValStr
            : JSON.parse(tokenValStr),
        };
      }
    }
  };

  static _getAuth0Tokens = (regexTest, returnAsString = false) => {
    console.log(`Auth0LocalStorageHelper => _getAuth0Tokens for ${regexTest.toString()}`);
    const foundTokens = [];
    for (let i = 0; i < localStorage.length; i++) {
      const tokenKey = localStorage.key(i);
      if (regexTest.test(tokenKey)) {
        console.log(`=> found Auth0 key match for ${regexTest.toString()} => ${tokenKey}`);
        const tokenValStr = localStorage.getItem(tokenKey);
        foundTokens.push({
          entryName: tokenKey,
          entryValue: returnAsString
            ? tokenValStr
            : JSON.parse(tokenValStr),
        });
      }
    }
    return foundTokens;
  };

  static get accessTokenEntry() {
    const regex = this._getAccessAndRefreshTokenRegex(
      sessionProperties.getClientId(),
      sessionProperties.getAudience(),
    );
    const token = this._getAuth0Token(regex);
    if (token) {
      return token;
    }
    // if not found, look for any token
    return this._getAuth0Token(this.accessAndRefreshTokenRegexGeneric);
  }

  static accessTokenEntryByAudience(audience) {
    const regex = this._getAccessAndRefreshTokenRegex(null, audience);
    const token = this._getAuth0Token(regex);
    return token;
  }

  static accessTokenEntryByClientAndAudience(clientId, audience) {
    const regex = this._getAccessAndRefreshTokenRegex(clientId, audience);
    const token = this._getAuth0Token(regex);
    return token;
  }

  static get identityTokenEntry() {
    return this._getAuth0Token(this.identityTokenRegex);
  }

  static _restoreAccessToken = (tokens, overwriteExisting) => {
    const { accessToken, refreshToken, entryName } = JSON.parse(tokens);
    console.log(`Auth0LocalStorageHelper => _restoreAccessToken for AT ${accessToken}`);
    console.log(`Auth0LocalStorageHelper => _restoreAccessToken for RT ${refreshToken}`);
    if (!overwriteExisting && this.accessTokenEntry) {
      console.log('Auth0LocalStorageHelper => _restoreAccessToken: access token already exists, aborting restore');
      return;
    }

    console.log('Auth0LocalStorageHelper => _restoreAccessToken: rebuilding access token localStorage entry...');
    const accessTokenBody = Auth0Utils.getJwtBodyFromToken(accessToken);
    // AUTH0-SPAJS SCHEMA FOR ACCESS AND REFRESH TOKEN ENTRY (single entry with both)
    const localStorageEntry = {
      body: {
        access_token: accessToken,
        audience: accessTokenBody.aud[0],
        client_id: accessTokenBody.azp,
        expires_in: accessTokenBody.exp - accessTokenBody.iat,
        oauthTokenScope: accessTokenBody.scope,
        refresh_token: refreshToken,
        scope: accessTokenBody.scope,
        token_type: 'Bearer',
      },
      expiresAt: accessTokenBody.exp,
    };
    // END SCHEMA
    const localStorageEntryVal = JSON.stringify(localStorageEntry);
    console.log(`Auth0LocalStorageHelper => _restoreAccessToken: rebuilt access token localStorage entry: ${localStorageEntryVal}`);
    console.log(`Auth0LocalStorageHelper => _restoreAccessToken: restoring to localStorage entry with key: ${entryName}`);
    if (Auth0Utils.isBrowser) {
      localStorage.setItem(entryName, localStorageEntryVal);
    }
    return {
      entryName,
      localStorageEntryVal,
    };
  };

  static _restoreIdentityToken = (token, overwriteExisting) => {
    const { identityToken, entryName } = JSON.parse(token);
    console.log(`Auth0LocalStorageHelper => _restoreIdentityToken ${identityToken}`);
    if (!overwriteExisting && this.identityToken) {
      console.log('Auth0LocalStorageHelper => _restoreIdentityToken: ID token already exists, aborting restore');
      return;
    }

    console.log('Auth0LocalStorageHelper => _restoreIdentityToken: rebuilding ID token localStorage entry...');
    const idTokenBody = Auth0Utils.getJwtBodyFromToken(identityToken);
    // AUTH0-SPAJS SCHEMA FOR IDENTITY TOKEN ENTRY
    const localStorageEntry = {
      decodedToken: {
        claims: {
          ...idTokenBody,
          __raw: identityToken,
        },
        encoded: {
          ...Auth0Utils.getEncodedJwtPartsFromToken(identityToken),
        },
        header: {
          ...Auth0Utils.getJwtHeaderFromToken(identityToken),
        },
        user: {
          ...Auth0Utils.getJwtUserDataFromToken(identityToken),
        },
      },
      id_token: identityToken,
    };
    // END SCHEMA
    const localStorageEntryVal = JSON.stringify(localStorageEntry);
    console.log(`Auth0LocalStorageHelper => _restoreIdentityToken: rebuilt identity token localStorage entry: ${localStorageEntryVal}`);
    console.log(`Auth0LocalStorageHelper => _restoreIdentityToken: restoring to localStorage entry with key: ${entryName}`);
    if (Auth0Utils.isBrowser) {
      localStorage.setItem(entryName, localStorageEntryVal);
    }
    return {
      entryName,
      localStorageEntryVal,
    };
  };

  static autoRestoreAuth0Tokens = (overwriteExisting = true, removeFragmentFromUrl = false) => {
    // auto detect and restore based on available data
    if (this.auth0TokenArgsInUrlFragment) {
      return this.restoreAuth0TokensFromUrlFragment(overwriteExisting, removeFragmentFromUrl);
    }
    return this.restoreAuth0TokensFromCookie(overwriteExisting);
  };

  static restoreAuth0TokensFromCookie = (overwriteExisting = true) => {
    console.log('Auth0LocalStorageHelper => restoreAuth0TokensFromCookie');

    const auth0StorageCookieAt = Auth0CookieHelper.getCookieValue(Auth0Constants.AUTH0_LOCALSTORAGE_COOKIE_NAME_AT);
    const auth0StorageCookieIdt = Auth0CookieHelper.getCookieValue(Auth0Constants.AUTH0_LOCALSTORAGE_COOKIE_NAME_IDT);
    if (!auth0StorageCookieAt || !auth0StorageCookieIdt) {
      console.log('Auth0LocalStorageHelper => restoreAuth0TokensFromCookie did not find any Auth0 entries to restore');
      return;
    }

    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_LOCALSTORAGE_COOKIE_NAME_AT);
    Auth0CookieHelper.deleteCookie(Auth0Constants.AUTH0_LOCALSTORAGE_COOKIE_NAME_IDT);

    return {
      accessTokenEntry: this._restoreAccessToken(auth0StorageCookieAt, overwriteExisting),
      identityTokenEntry: this._restoreIdentityToken(auth0StorageCookieIdt, overwriteExisting),
    };
  };

  static get auth0TokenArgsInUrlFragment() {
    if (!window.location.hash) {
      return;
    }
    const hash = window.location.hash.substring(1); // Removes the '#'
    // Parse the hash like a query string using URLSearchParams
    const tokenParams = new URLSearchParams(hash);
    // Convert to JS object
    const tokens = {};
    tokenParams.forEach((value, key) => {
      tokens[key] = value;
    });

    const tokenObj = {
      auth0UrlArgAt: tokens[Auth0Constants.AUTH0_LOCALSTORAGE_COOKIE_NAME_AT],
      auth0UrlArgIdt: tokens[Auth0Constants.AUTH0_LOCALSTORAGE_COOKIE_NAME_IDT],
    };

    return tokenObj.auth0UrlArgAt && tokenObj.auth0UrlArgIdt ? tokenObj : null;
  }

  static restoreAuth0TokensFromUrlFragment = (overwriteExisting = true, removeFragmentFromUrl = false) => {
    console.log('Auth0LocalStorageHelper => restoreAuth0TokensFromUrlFragment');
    const tokenArgs = this.auth0TokenArgsInUrlFragment;
    if (!tokenArgs) {
      throw new Error(`restoreAuth0TokensFromUrlFragment cannot restore Auth0 local storage data because the provided url (${window.location.hash}) does not have any URL fragment`);
    }

    if (!tokenArgs.auth0UrlArgAt || !tokenArgs.auth0UrlArgIdt) {
      throw new Error(`restoreAuth0TokensFromUrlFragment cannot restore Auth0 local storage data because the provided url (${window.location.hash}) does not have any Auth0 token values in the URL fragment`);
    }

    // now remove the processed fragment from the url
    if (removeFragmentFromUrl) {
      window.history.replaceState(null, null, window.location.pathname + window.location.search);
    }

    return {
      accessTokenEntry: this._restoreAccessToken(tokenArgs.auth0UrlArgAt, overwriteExisting),
      identityTokenEntry: this._restoreIdentityToken(tokenArgs.auth0UrlArgIdt, overwriteExisting),
    };
  };

  static setSessionClientId = (clientId) => {
    sessionProperties.setClientId(clientId);
  };

  static setSessionAudience = (audience) => {
    sessionProperties.setAudience(audience);
  };
}

export default Auth0LocalStorageHelper;
